import SiteHeader from '@/components/feature/SiteHeader';
import SiteFooter from '@/components/feature/SiteFooter';
import PageHero from '@/components/feature/PageHero';

const programRows = [
  ['Program', 'MatchLineup team texts'],
  ['Sent by', 'Penland LLC, doing business as MatchLineup'],
  ['Recipients', 'Adult tennis players on a team roster who opted in themselves'],
  ['Message types', 'Match availability requests, reminders for players who have not answered, lineup assignments, and match time changes or cancellations'],
  ['Frequency', "Varies with the team's schedule, typically 2–8 messages per month"],
  ['Cost', 'Message and data rates may apply'],
  ['Opt out', 'Reply STOP at any time, or turn texts off on the personal opt-in page'],
  ['Help', 'Reply HELP, or email support@penlandllc.com'],
  ['Number', '(804) 376-1754'],
];

const sampleMessages = [
  'MatchLineup: [Team] vs [Opponent], Sat 9/12 9:00 AM (Home). Can you play? Tap to respond: https://matchlineup.com/a/[token] Reply STOP to opt out.',
  'MatchLineup reminder: your captain still needs your answer for [Team] vs [Opponent], Sat 9/12 9:00 AM. Tap: https://matchlineup.com/a/[token] Reply STOP to opt out.',
  "MatchLineup lineup: [Team] vs [Opponent], Sat 9/12 9:00 AM (Home). You're on Line 2 Doubles with [Partner]. Reply STOP to opt out.",
  'MatchLineup update: [Team] vs [Opponent] is now Sun 9/13 10:00 AM (Away). Your response link still works: https://matchlineup.com/a/[token] Reply STOP to opt out.',
  'MatchLineup: [Team] vs [Opponent] on Sat 9/12 is CANCELLED. No need to respond. Reply STOP to opt out.',
];

export default function MatchLineup() {
  return (
    <main className="min-h-screen">
      <SiteHeader />
      <PageHero
        eyebrow="Software by Penland LLC"
        title="Match"
        titleAccent="Lineup"
        subtitle="Match scheduling for adult recreational tennis teams."
        image="https://readdy.ai/api/search-image?query=Stylized%20abstract%20tennis%20court%20aerial%20view%20with%20clean%20geometric%20lines%20in%20teal%20and%20navy%20tones%2C%20minimal%20sport%20backdrop%20with%20soft%20light%20and%20elegant%20composition&width=1600&height=900&seq=matchlineup-hero-01&orientation=landscape"
        imageAlt="Abstract tennis court lines representing MatchLineup"
      />

      {/* Part 1 */}
      <section className="bg-background-50 py-16 md:py-24">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-accent-600 text-sm font-semibold tracking-widest uppercase mb-3">
              Match Scheduling
            </p>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-primary-950 leading-tight mb-6">
              Lineups, sorted out
              <span className="italic text-accent-600"> for captains and players</span>
            </h2>
            <p className="text-foreground-600 text-base md:text-lg leading-relaxed mb-8">
              Captains post matches, players say whether they can play, and captains set
              lineups. Players who turn texts on get those requests by text.
            </p>
            <a
              href="https://matchlineup.com"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-accent-500 hover:bg-accent-600 text-primary-950 text-base md:text-lg font-bold px-9 py-4 rounded-full transition-all whitespace-nowrap"
            >
              Open MatchLineup
              <i className="ri-external-link-line" />
            </a>
          </div>
        </div>
      </section>

      {/* Part 2 - Text message disclosure (exact wording) */}
      <section className="bg-background-100 py-16 md:py-24">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-heading text-3xl md:text-4xl text-primary-950 font-semibold mb-6">
              Text messages
            </h2>

            <p className="text-foreground-700 text-base leading-relaxed mb-5">
              Adding a player to a roster does not send that player any text messages. Texts
              start only after the player opts in personally, in one of two ways: on their
              personal opt-in page, or by texting START to (804) 376-1754 from their own phone.
            </p>

            <p className="text-foreground-700 text-base leading-relaxed mb-5">
              The captain gives each player a personal opt-in link in person, by email, or in
              the team&apos;s group chat. The link is never sent by text message. On that page
              the player enters their own mobile number and checks an unchecked box that reads:
            </p>

            <div className="border border-background-300 rounded-xl bg-background-50 p-5 md:p-6 mb-6">
              <div className="flex items-start gap-3">
                <span
                  className="w-5 h-5 rounded-[4px] border-2 border-foreground-400 shrink-0 mt-0.5"
                  aria-hidden="true"
                />
                <p className="text-foreground-700 text-sm md:text-base leading-relaxed">
                  Yes &mdash; text me about my tennis team. By checking this box I agree to
                  receive SMS messages from MatchLineup with match availability requests,
                  reminders, and lineup assignments. Message frequency varies (typically
                  2&ndash;8 per month). Msg &amp; data rates may apply. Reply STOP to opt out at
                  any time, HELP for help. See our{' '}
                  <a
                    href="https://matchlineup.com/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-accent-600 hover:text-accent-500 font-medium underline"
                  >
                    Privacy Policy
                  </a>{' '}
                  and{' '}
                  <a
                    href="https://matchlineup.com/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-accent-600 hover:text-accent-500 font-medium underline"
                  >
                    Terms
                  </a>
                  .
                </p>
              </div>
            </div>

            <p className="text-foreground-700 text-base leading-relaxed mb-10">
              A live demo of that page is at{' '}
              <a
                href="https://matchlineup.com/opt-in/demo"
                target="_blank"
                rel="noopener noreferrer"
                className="text-accent-600 hover:text-accent-500 font-medium underline break-all"
              >
                matchlineup.com/opt-in/demo
              </a>
              . Captains cannot opt a player in. Consent to receive texts is not a condition of
              using MatchLineup or of any purchase.
            </p>

            {/* Program table */}
            <div className="overflow-x-auto mb-10">
              <table className="w-full border border-background-200 rounded-xl overflow-hidden text-left">
                <tbody>
                  {programRows.map((row, i) => (
                    <tr
                      key={row[0]}
                      className={i % 2 === 0 ? 'bg-background-50' : 'bg-background-200/40'}
                    >
                      <th
                        scope="row"
                        className="align-top text-primary-950 text-sm font-semibold px-4 md:px-6 py-3.5 w-40 md:w-52 border-b border-background-200/60"
                      >
                        {row[0]}
                      </th>
                      <td className="align-top text-foreground-600 text-sm px-4 md:px-6 py-3.5 border-b border-background-200/60">
                        {row[1]}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Sample messages */}
            <h3 className="font-heading text-xl md:text-2xl text-primary-950 font-semibold mb-4">
              Sample messages
            </h3>
            <div className="space-y-4 mb-10">
              {sampleMessages.map((msg, i) => (
                <div
                  key={i}
                  className="bg-background-50 border border-background-200 rounded-2xl rounded-tl-sm px-5 py-4 max-w-2xl"
                >
                  <p className="text-foreground-700 text-sm leading-relaxed break-words">
                    {msg}
                  </p>
                </div>
              ))}
            </div>

            <p className="text-foreground-700 text-base leading-relaxed mb-6">
              The full program terms are in the{' '}
              <a
                href="https://matchlineup.com/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="text-accent-600 hover:text-accent-500 font-medium underline"
              >
                MatchLineup Terms
              </a>
              , and how phone numbers are handled is in the{' '}
              <a
                href="https://matchlineup.com/privacy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-accent-600 hover:text-accent-500 font-medium underline"
              >
                MatchLineup Privacy Policy
              </a>
              . Mobile numbers and opt-in consent are never shared with third parties or
              affiliates for marketing or promotional purposes.
            </p>

            <div className="border-t border-background-200 pt-6">
              <p className="text-foreground-600 text-sm leading-relaxed">
                Players with questions about a team should contact their captain. For anything
                about MatchLineup itself or its text messages, email{' '}
                <a
                  href="mailto:support@penlandllc.com"
                  className="text-accent-600 hover:text-accent-500 font-medium underline"
                >
                  support@penlandllc.com
                </a>
                .
              </p>
            </div>
          </div>
        </div>
      </section>

      <SiteFooter />
    </main>
  );
}